import React from 'react';
import { Link } from 'react-router-dom';
import { IconChevronRight } from '@tabler/icons-react';

/** A single segment in the breadcrumb trail. */
export interface BreadcrumbItem {
  /** Display text for this segment. */
  label: string;
  /** Navigation path. Omit for the current (last) item. */
  href?: string;
}

/** Props for {@link AdminBreadcrumb}. */
export interface AdminBreadcrumbProps {
  /** Ordered list of breadcrumb segments. Last item is treated as current page. */
  items: BreadcrumbItem[];
}

/**
 * Breadcrumb trail for admin page headers.
 * Renders clickable ancestor links separated by chevrons,
 * with the final item styled as the current page.
 */
export const AdminBreadcrumb: React.FC<AdminBreadcrumbProps> = ({ items }) => {
  if (items.length === 0) return null;

  return (
    <nav aria-label="Breadcrumb" className="flex items-center gap-1 text-xs mt-1 pb-1">
      {items.map((item, i) => {
        const isLast = i === items.length - 1;
        return (
          <React.Fragment key={i}>
            {i > 0 && (
              <IconChevronRight className="w-3 h-3 text-subtle-foreground shrink-0" />
            )}
            {item.href && !isLast ? (
              <Link
                to={item.href}
                className="text-muted-foreground hover:text-foreground transition-colors truncate max-w-[200px]"
              >
                {item.label}
              </Link>
            ) : (
              <span className="text-muted-foreground truncate max-w-[200px]">
                {item.label}
              </span>
            )}
          </React.Fragment>
        );
      })}
    </nav>
  );
};
